/**
 * Cart Fabric Display Handler
 * Converts cart quantities back to meters for display
 * Works with cart drawer, cart page, and checkout
 */

(function() {
  'use strict';

  class CartFabricDisplay {
    constructor() {
      this.multiplier = 2; // Same as in fabric-pricing.js (for 0.5 increment)
      this.init();
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.updateCartDisplay());
      } else {
        this.updateCartDisplay();
      }

      // Listen for cart updates
      document.addEventListener('cart:updated', () => this.updateCartDisplay());
      document.addEventListener('cart:refresh', () => this.updateCartDisplay());
      
      // For AJAX cart updates
      if (typeof Shopify !== 'undefined' && Shopify.theme) {
        document.addEventListener('shopify:section:load', () => {
          setTimeout(() => this.updateCartDisplay(), 100);
        });
      }
    }

    updateCartDisplay() {
      // Update cart page items
      this.updateCartPageItems();
      
      // Update cart drawer items
      this.updateCartDrawerItems();
      
      // Update cart quantity inputs
      this.updateQuantityInputs();
    }

    updateCartPageItems() {
      // Find all cart items
      const cartItems = document.querySelectorAll('[data-cart-item], .cart-item, .cart__row');
      
      cartItems.forEach(item => {
        this.processCartItem(item);
      });
    }

    updateCartDrawerItems() {
      // For cart drawer
      const drawerItems = document.querySelectorAll('.drawer__cart-item, .ajax-cart__item, .mini-cart__item');
      
      drawerItems.forEach(item => {
        this.processCartItem(item);
      });
    }

    processCartItem(itemElement) {
      // Check if item has unit price measurement (fabric product)
      const hasFabricIndicator = itemElement.querySelector('[data-fabric-item]') || 
                                  itemElement.querySelector('.fabric-item') ||
                                  itemElement.innerHTML.includes('meter') ||
                                  itemElement.innerHTML.includes('metre');

      if (!hasFabricIndicator) return;

      // Find quantity display/input
      const qtyInput = itemElement.querySelector('input[name="updates[]"], input.cart-qty, .quantity__input');
      const qtyDisplay = itemElement.querySelector('.cart-item__quantity, .cart__quantity-value, [data-cart-quantity]');

      if (qtyInput) {
        const cartQty = parseInt(qtyInput.value) || 0;
        const actualMeters = cartQty / this.multiplier;

        // Update input value
        const originalValue = qtyInput.value;
        qtyInput.setAttribute('data-original-qty', originalValue);
        qtyInput.value = actualMeters;

        // Add meter label if not exists
        if (!itemElement.querySelector('.meter-label')) {
          const meterLabel = document.createElement('span');
          meterLabel.className = 'meter-label';
          meterLabel.textContent = ' meters';
          meterLabel.style.cssText = 'margin-left: 5px; font-size: 13px; color: #888;';
          
          if (qtyInput.parentElement) {
            qtyInput.parentElement.appendChild(meterLabel);
          }
        }

        // Store the actual quantity for form submission
        qtyInput.addEventListener('change', (e) => {
          const newMeters = parseFloat(e.target.value) || 1;
          const newCartQty = Math.round(newMeters * this.multiplier);
          e.target.setAttribute('data-original-qty', newCartQty);
          
          // Update the actual form value on submit
          const form = e.target.closest('form');
          if (form) {
            form.addEventListener('submit', (submitEvent) => {
              e.target.value = newCartQty;
            }, { once: true });
          }
        });
      }

      if (qtyDisplay) {
        const cartQty = parseInt(qtyDisplay.textContent) || 0;
        const actualMeters = cartQty / this.multiplier;
        qtyDisplay.textContent = actualMeters;
        
        if (!itemElement.querySelector('.meter-label-display')) {
          const meterLabel = document.createElement('span');
          meterLabel.className = 'meter-label-display';
          meterLabel.textContent = ' meters';
          meterLabel.style.cssText = 'margin-left: 3px; font-size: 13px; color: #888;';
          qtyDisplay.appendChild(meterLabel);
        }
      }
    }

    updateQuantityInputs() {
      // Handle quantity increment/decrement buttons
      const quantityWrappers = document.querySelectorAll('[data-fabric-item] .quantity, .cart-item .quantity');

      quantityWrappers.forEach(wrapper => {
        const input = wrapper.querySelector('input[name="updates[]"], .quantity__input');
        const plusBtn = wrapper.querySelector('[data-quantity-plus], .quantity__button.plus');
        const minusBtn = wrapper.querySelector('[data-quantity-minus], .quantity__button.minus');

        if (!input) return;

        // Check if this is a fabric item
        const cartItem = input.closest('[data-cart-item], .cart-item, .cart__row');
        if (!cartItem || !this.isFabricItem(cartItem)) return;

        if (plusBtn) {
          plusBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const currentMeters = parseFloat(input.value) || 1;
            const newMeters = currentMeters + 0.5;
            input.value = newMeters;
            this.updateCart(input, newMeters);
          });
        }

        if (minusBtn) {
          minusBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const currentMeters = parseFloat(input.value) || 1;
            const newMeters = Math.max(0.5, currentMeters - 0.5);
            input.value = newMeters;
            this.updateCart(input, newMeters);
          });
        }
      });
    }

    isFabricItem(itemElement) {
      return itemElement.querySelector('[data-fabric-item]') || 
             itemElement.innerHTML.includes('meter') ||
             itemElement.innerHTML.includes('metre');
    }

    updateCart(input, meters) {
      const cartQty = Math.round(meters * this.multiplier);
      const variantId = input.getAttribute('data-index') || input.getAttribute('id');
      
      // Prepare for form submission
      input.setAttribute('data-original-qty', cartQty);
      
      // If AJAX cart is enabled, update immediately
      if (typeof fetch !== 'undefined') {
        const formData = new FormData();
        formData.append('updates[]', cartQty);
        formData.append('id', variantId);

        fetch('/cart/update.js', {
          method: 'POST',
          body: formData
        })
        .then(response => response.json())
        .then(cart => {
          console.log('Cart updated:', cart);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        })
        .catch(error => {
          console.error('Cart update error:', error);
        });
      }
    }
  }

  // Initialize
  window.cartFabricDisplay = new CartFabricDisplay();

})();